import { Flag } from '@components/display/images';
import { ITableProps, Table } from '@components/display/tables';
import { IInbox } from '@inbox/types';
import { Chip, TableCell, Typography } from '@mui/material';
import { TableHeader, TableRowOption } from '@common/types/common';
import { useInboxContext } from '@inbox/contexts/InboxContext';
import {
    handleActivateError,
    handleActivateSuccess,
    handleDeactivateError,
    handleDeactivateSuccess,
    handleDeleteError,
    handleDeleteSuccess,
    handleNoInboxIdError,
} from '@inbox/utils';

type InboxTableHeader = Pick<IInbox, 'email' | 'countries' | 'is_active' | 'notes'>;

const INBOX_TABLE_HEADERS: TableHeader<InboxTableHeader>[] = [
    { id: 'email', label: 'EMAIL ADDRESS', sortable: true },
    { id: 'countries', label: 'COUNTRIES', sortable: false },
    { id: 'is_active', label: 'STATUS', sortable: false },
    { id: 'notes', label: 'NOTES', sortable: false },
];

const ROWS_PER_PAGE_OPTIONS = [
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
];

type InboxManagementTableProps = Pick<
    ITableProps<IInbox, InboxTableHeader>,
    | 'data'
    | 'isLoading'
    | 'page'
    | 'onPageChange'
    | 'rowsPerPage'
    | 'setRowsPerPage'
    | 'count'
    | 'sort'
    | 'onSortClick'
    | 'className'
> & { querysetIds: number[] };

const InboxManagementTable = (props: InboxManagementTableProps) => {
    const { querysetIds, ...tableProps } = props;
    const {
        onDelete,
        onToggleActive,
        onToggleInactive,
        onOpenEditModal: handleOpenEditModal,
        selectedItems,
        toggleSelectedItem,
        updateSelectedItems,
        removeSelectedItems,
        isActivating = false,
        isDeactivating = false,
        isDeleting = false,
        managedCountries = [],
    } = useInboxContext();

    const handleRowCheckClick = (row: IInbox) => toggleSelectedItem(row.id);
    const handleSelectAll = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        updateSelectedItems(checked ? querysetIds : []);
    };

    const isDataPending = () => isActivating || isDeactivating || isDeleting;

    const canManageCountry = (item: Partial<IInbox>) => {
        if (!item.countries) return false;
        return item.countries.some((country) =>
            managedCountries.some((managedCountry) => managedCountry.code === country.code)
        );
    };

    const isMenuDisabled = (item: Partial<IInbox>) => !canManageCountry(item) || isDataPending();

    const handleToggleActive = async (item: Partial<IInbox>) => {
        if (!item.id) {
            handleNoInboxIdError();
            return;
        }
        try {
            await onToggleActive(item);
            handleActivateSuccess([item.id]);
        } catch {
            handleActivateError([item.id]);
        }
    };

    const handleToggleInactive = async (item: Partial<IInbox>) => {
        if (!item.id) {
            handleNoInboxIdError();
            return;
        }
        try {
            await onToggleInactive(item);
            handleDeactivateSuccess([item.id]);
        } catch {
            handleDeactivateError([item.id]);
        }
    };

    const handleDelete = async (item: Partial<IInbox>) => {
        if (!item.id) {
            handleNoInboxIdError();
            return;
        }
        try {
            await onDelete(item);
            removeSelectedItems([item.id]);
            handleDeleteSuccess([item.id]);
        } catch {
            handleDeleteError([item.id]);
        }
    };

    const rowOptions: TableRowOption<IInbox>[] = [
        { label: 'Edit Sales Inbox', onClick: handleOpenEditModal, disabled: isMenuDisabled },
        {
            label: 'Change Status to Active',
            onClick: handleToggleActive,
            disabled: isMenuDisabled,
        },
        {
            label: 'Change Status to Inactive',
            onClick: handleToggleInactive,
            disabled: isMenuDisabled,
        },
        { label: 'Remove', onClick: handleDelete, disabled: isMenuDisabled },
    ];

    const renderRow = (item: IInbox) => (
        <>
            <TableCell>{item.email}</TableCell>
            <TableCell>
                <div className="flex gap-1 flex-wrap">
                    {item.countries.map((country) => (
                        <Flag
                            key={country.code}
                            flag={country.code.toLowerCase()}
                            className="w-[27px] h-[18px] rounded"
                        />
                    ))}
                </div>
            </TableCell>
            <TableCell>
                <Chip
                    variant={item.is_active ? 'status-success' : 'status-error'}
                    label={item.is_active ? 'Active' : 'Inactive'}
                />
            </TableCell>
            <TableCell>
                <Typography className="whitespace-nowrap">{item.notes}</Typography>
            </TableCell>
        </>
    );

    return (
        <Table
            pagination
            selectable
            id="inbox-management-table"
            rowOptions={{ data: rowOptions }}
            headers={INBOX_TABLE_HEADERS}
            fillMissingRows={false}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            emptyListTxt="No inboxes to display"
            renderRow={renderRow}
            onRowClick={handleOpenEditModal}
            selectedItems={selectedItems}
            onRowCheckClick={handleRowCheckClick}
            onSelectAll={handleSelectAll}
            {...tableProps}
        />
    );
};

export { InboxManagementTable };
